
import { Injectable } from '@angular/core';

/**
 * Service for enhanced logging with timestamps and performance metrics
 */
@Injectable({
  providedIn: 'root'
})
export class LoggingService {
  private perfMarkers: Map<string, number> = new Map();
  private enableDebug = false;
  
  constructor() {
    // Check if we're in development mode or debug is enabled
    this.enableDebug = !this.isProduction();
  }
  
  /**
   * Log information with timestamp
   * @param source Source of the log (e.g., 'ChatService', 'WebSocket')
   * @param message Message to log or data object
   * @param data Optional data to include
   */
  info(source: string, message: string | any, data?: any): void {
    const timestamp = this.formatTimestamp(new Date());
    
    // Handle case where message is actually data
    if (typeof message === 'object' && !data) {
      const logMessage = `[${timestamp}][${source}]`;
      console.log(logMessage, message);
    } else {
      const logMessage = `[${timestamp}][${source}] ${message}`;
      if (data) {
        console.log(logMessage, data);
      } else {
        console.log(logMessage);
      }
    }
  }
  
  /**
   * Log errors with timestamp
   * @param source Source of the error
   * @param message Error message or error object
   * @param error Error object or details
   */
  error(source: string, message: string | any, error?: any): void {
    const timestamp = this.formatTimestamp(new Date());
    
    // Handle case where message is actually error object
    if (typeof message === 'object' && !error) {
      const logMessage = `[${timestamp}][${source}][ERROR]`;
      console.error(logMessage, message);
    } else {
      const logMessage = `[${timestamp}][${source}][ERROR] ${message}`;
      if (error) {
        console.error(logMessage, error);
      } else {
        console.error(logMessage);
      }
    }
  }
  
  /**
   * Log warnings with timestamp
   * @param source Source of the warning
   * @param message Warning message or data object
   * @param data Additional data
   */
  warn(source: string, message: string | any, data?: any): void {
    const timestamp = this.formatTimestamp(new Date());

    // Handle case where message is actually data
    if (typeof message === 'object' && !data) {
      const logMessage = `[${timestamp}][${source}][WARN]`;
      console.warn(logMessage, message);
    } else {
      const logMessage = `[${timestamp}][${source}][WARN] ${message}`;
      if (data) {
        console.warn(logMessage, data);
      } else {
        console.warn(logMessage);
      }
    }
  }
  
  /**
   * Log debug messages (only in development mode)
   * @param source Source of the debug message
   * @param message Debug message
   * @param data Additional data
   */
  debug(source: string, message: string, data?: any): void {
    if (!this.enableDebug) return;
    
    const timestamp = this.formatTimestamp(new Date());
    const logMessage = `[${timestamp}][${source}][DEBUG] ${message}`;
    
    if (data) {
      console.debug(logMessage, data);
    } else {
      console.debug(logMessage);
    }
  }
  
  /**
   * Start timing a specific operation
   * @param markerId Unique identifier for the timing operation
   */
  startTimer(markerId: string): void {
    this.perfMarkers.set(markerId, performance.now());
  }
  
  /**
   * End timing an operation and log the duration
   * @param markerId Identifier used in startTimer
   * @param source Source for the log
   * @param operation Description of the operation
   * @returns Duration in milliseconds
   */
  endTimer(markerId: string, source: string, operation: string): number {
    const startTime = this.perfMarkers.get(markerId);
    if (startTime === undefined) {
      this.warn(source, `Timer "${markerId}" was never started`);
      return 0;
    }
    
    const duration = performance.now() - startTime;
    this.info(source, `${operation} completed in ${duration.toFixed(2)}ms`);
    this.perfMarkers.delete(markerId);
    return duration;
  }
  
  /**
   * Format a timestamp for logging
   * @param date Date to format
   * @returns Formatted timestamp
   */
  private formatTimestamp(date: Date): string {
    // Format: HH:MM:SS.mmm
    return date.toISOString().split('T')[1].substring(0, 12);
  }
  
  /**
   * Check if the application is running in production mode
   */
  private isProduction(): boolean {
    try {
      // In Angular, this would typically be injected
      // This is a simple fallback
      return window.location.hostname !== 'localhost' &&
             !window.location.hostname.startsWith('127.0.0.1');
    } catch (e) {
      return false;
    }
  }

  /**
   * Log structured data (JSON format) - useful for E2E test analysis
   * @param component Component name (e.g., 'ChatService', 'WebSocket')
   * @param eventType Type of event (e.g., 'HTTP-REQUEST', 'HTTP-RESPONSE', 'WS-MESSAGE')
   * @param data Structured data object
   */
  logStructured(component: string, eventType: string, data: any): void {
    const timestamp = this.formatTimestamp(new Date());
    const testContext = this.getTestContext();

    const structuredLog = {
      timestamp,
      component,
      eventType,
      testContext,
      ...data
    };

    // In E2E test mode, output as JSON for programmatic parsing
    if (this.isE2ETest()) {
      console.log(JSON.stringify(structuredLog));
    } else {
      // In dev mode, output as readable log
      console.log(`[${timestamp}][${component}][${eventType}]`, data);
    }
  }

  /**
   * Check if running in E2E test mode
   */
  private isE2ETest(): boolean {
    try {
      const win = window as any;
      return win.__PLAYWRIGHT_TEST__ === true ||
             win.playwright !== undefined ||
             navigator.webdriver === true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get test context if running in E2E test
   */
  private getTestContext(): TestContext | null {
    if (!this.isE2ETest()) {
      return null;
    }

    const win = window as any;
    return {
      testName: win.__TEST_NAME__ || 'unknown',
      testFile: win.__TEST_FILE__ || 'unknown'
    };
  }
}

/**
 * Test context interface
 */
interface TestContext {
  testName: string;
  testFile: string;
}